import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import fileDownload from 'js-file-download';
import { NGXLogger } from 'ngx-logger';
import * as pdf from 'pdfjs-dist';
import { GlobalWorkerOptions } from 'pdfjs-dist';
import { Table } from 'primeng/table';
import { forkJoin, from, Observable, of, Subject } from 'rxjs';
import { catchError, map, mergeMap, takeUntil } from 'rxjs/operators';
import { FileCenterActionType } from '../../../../enum/src/lib/file-center-action-type.enum';
import { ErrorDialogMessage } from '../../../../model/src/lib/error-dialog-message.model';
import { FileInfo } from '../../../../model/src/lib/file-info.model';
import { UploadFile } from '../../../../model/src/lib/upload-file.model';
import { getFileCategoryCLassNameByExtension } from '../../../../util/src/lib/get-file-category-class-name-by-extension';
import { getImagePreviewSrcByBlob } from '../../../../util/src/lib/get-image-preview-src-by-blob';
import { isImageFileExtension } from '../../../../util/src/lib/is-image-file-extension';
import { AppConfig } from '../../../config/app.config';
import { ApiFileCenterService } from '../../../core/services/api-file-center.service';
import { SystemDialogService } from '../../../core/services/system-dialog.service';
import { ToastService } from '../../../core/services/toast.service';
import { UserProfileService } from '../../../core/services/user-profile.service';
import { HttpClient, HttpResponse } from '@angular/common/http';
/**
 * @description 檔案中心組件，提供檔案上傳、下載、編輯清單等功能。必需設定 domain 與 subDomain 提供檔案中心 api 使用。另外可指定編輯或檢視模式，預設為編輯模式。
 *
 * Sample:
 *
 * <sigv-file-center
 *
 * domain="CreditRatingScales(Required)"
 *
 * subDomain="Attachment(Required)"
 *
 * [action]="FileCenterActionType.EDIT(Required EDIT or VIEW)"
 *
 * [accept]="'.jpg, .jpeg, .png, .pdf, .doc, .docx, .xls, .xlsx, .txt, .zip'"
 *
 * [fileDetailIds]="fileDetailIds"
 *
 * (attachmentsChange)="attachmentsChangeHanlder($event)"
 *
 * ></sigv-file-center>
 */
const IMG_EXT_ICON_MAP: Map<string, string> = new Map<string, string>([
  ['png', 'icon-png'],
  ['jpg', 'icon-jpg'],
  ['gif', 'icon-gif'],
  ['jpeg', 'icon-jpeg'],
]);

@Component({
  selector: 'sigv-file-center',
  templateUrl: './file-center.component.html',
  styleUrls: ['./file-center.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FileCenterComponent implements OnInit, OnDestroy {
  @ViewChildren('pdfCanvas') pdfCanvases!: QueryList<
    ElementRef<HTMLCanvasElement>
  >;

  /**
   * @description 上傳檔案中心時使用的 doamin 設定(必填)
   */
  @Input()
  domain = '';

  /**
   * @description 上傳檔案中心時使用的 subDomain 設定(必填)
   */
  @Input()
  subDomain = '';

  /**
   * @description 提供指定檔案清單中特定項目為唯讀狀態，設定值為檔案中心記錄的 guid
   */
  @Input()
  fileDetailIdsViewOnly: string[] = [];

  /**
   * @description 提供指定檔案清單中特定項目為不可下載狀態，設定值為檔案中心記錄的 guid
   */
  @Input()
  fileDetailIdsDisableDownload: string[] = [];

  /**
   * @description 指定使用模式，目前提供編輯與檢視模式，預設為編輯模式
   */
  @Input()
  action: FileCenterActionType = FileCenterActionType.EDIT;

  /**
   * @description 是否支援多筆檔案
   */
  @Input()
  multiple = true;

  private defaultFileDetailIds: string[] = [];

  /**
   * @description 初始載入檔案清單，非雙向綁定欄位
   */
  @Input()
  set fileDetailIds(value: string[]) {
    this.defaultFileDetailIds = value;
    const newItems = value.filter(
      (f) =>
        !this.attachments
          .map((attachment) => attachment.fileDetailId)
          .includes(f)
    );
    if (newItems && newItems.length > 0) {
      this.getFileDetails(newItems).subscribe((files) => {
        if (files && files.length > 0) {
          this.attachments.push(...files);
          this.updateTableStatus();
          this.attachmentsChange.emit(this.attachments);
          this.changeDetectorRef.markForCheck();
        }
      });
    }
  }

  get fileDetailIds(): string[] {
    return this.defaultFileDetailIds;
  }

  /**
   * @description 允許檔案上傳的檔案類型，預設為 .jpg, .jpeg, .png, .gif, .pdf, .doc, .docx, .xls, .xlsx, .txt, .zip
   */
  @Input()
  accept =
    '.jpg, .jpeg, .png, .gif, .pdf, .doc, .docx, .xls, .xlsx, .txt, .zip, .tif, .tiff';

  /**
   * @description 是否顯示下載按鈕, 只有在 View Mode 此設定才有效果
   */
  @Input()
  hasDownloadButton = false;

  /**
   * @description 全部下載檔案的檔案名稱，預設檔名為 attachment.zip
   */
  @Input()
  downloadBundleFileName = 'attachment.zip';

  /**
   * @description 檔案上傳上限，預設值為 20，單位為 MB
   */
  @Input()
  uploadFileSizeLimit = 20;

  /**
   * @description 設定在沒有資料時是否顯示 table，預設為 true
   */
  @Input()
  isShowNoDataTable = true;

  /**
   * 上傳檔名客製化format函式
   * @description 若有設定此函式，則會使用此函式來格式化上傳檔名，
   * 若沒有設定則使用預設的檔名格式化方式，預設為使用檔案名稱
   * @param fileInfo 檔案資訊
   * @param fileIndex 檔案索引位置
   * @param fileIndex 副檔名
   */
  @Input() customFormatFileName?: (
    fileInfo: File,
    fileIndex: number,
    extension: string
  ) => string | null = (fileInfo) => fileInfo.name ?? null;

  /**
   * @description 檔案清單發生變動時(Upload or Delete)觸發事件，傳入參數為清單檔案資訊(FileInfo)
   */
  @Output()
  attachmentsChange = new EventEmitter<FileInfo[]>();

  /**
   * @description 點載連結成功下載後觸發事件，提供所有被下載檔案的 id
   */
  @Output()
  fileDownloaded = new EventEmitter<string[]>();

  public get colspanLength(): number {
    return !this.isViewMode || (this.hasDownloadButton && this.isViewMode)
      ? 6
      : 5;
  }

  destroy$ = new Subject();

  attachments: FileInfo[] = [];
  selectedAttachments: FileInfo[] = [];
  clonedAttachments: { [s: string]: FileInfo } = {};
  uploadFiles: UploadFile[] = [];
  uploadFilesForm: FormGroup;

  dateFormat = '';
  isSelectedSomeSelectableRow = false;
  hasEditableAttachments?: boolean;

  isShowFilesUploadDialog = false;

  // galleria 相關
  galleriaActiveIndex = 0;
  attachmentImages: FileInfo[] = [];
  private _isShowGalleria = false;
  galleriaReponsiveSetting: any[] = [
    {
      breakpoint: '1500px',
      numVisible: 5,
    },
    {
      breakpoint: '1024px',
      numVisible: 3,
    },
    {
      breakpoint: '768px',
      numVisible: 2,
    },
    {
      breakpoint: '560px',
      numVisible: 1,
    },
  ];

  // R202412065178 新增圖片縮放功能
  zoomLevel: number = 1;
  isDragging: boolean = false;
  startX: number = 0;
  startY: number = 0;
  translateX: number = 0;
  translateY: number = 0;
  lastTranslateX: number = 0;
  lastTranslateY: number = 0;
  containerWidth: number = 0;
  containerHeight: number = 0;
  imageWidth: number = 0;
  imageHeight: number = 0;
  rotation: number = 0;

  // 控制PDF預覽視窗是否開啟
  pdfDoc!: any | null;
  showPdfPreview = false;
  currentPDFPage = 1;
  scalePDF = 1;
  rotationPDF = 0;
  isRendering = false;

  get displayGalleria(): boolean {
    return this._isShowGalleria;
  }

  set displayGalleria(value: boolean) {
    this._isShowGalleria = value;

    if (!value) {
      this.resetImage(this.galleriaActiveIndex);
    }
  }

  @ViewChild('uploadFilesInput')
  uploadFilesInput!: ElementRef;

  @ViewChild('attachmentTable')
  attachmentTable?: Table;

  constructor(
    private sanitizer: DomSanitizer,
    private userProfileService: UserProfileService,
    private apiFileCenterService: ApiFileCenterService,
    private translateService: TranslateService,
    private toastService: ToastService,
    private dialogService: SystemDialogService,
    private logger: NGXLogger,
    private changeDetectorRef: ChangeDetectorRef,
    private appConfig: AppConfig,
    private http: HttpClient
  ) {
    this.uploadFilesForm = new FormGroup({});

    // 日期格式設定，由 env.json 取得，避免程式出錯預設台灣格式
    this.dateFormat = this.appConfig.format.date || 'yyyy/MM/dd';

    // 確保正確的 Worker 路徑
    GlobalWorkerOptions.workerSrc = '/pdf.worker.js';
  }

  ngOnInit(): void {
    // 必填 @Input 檢查，避免誤用組件
    if (!this.domain || !this.subDomain) {
      throw new Error(
        'sigv-file-center component required domain and subDomain setting.'
      );
    }

    // 必填 @Input 檢查，避免誤用組件
    if (this.action === FileCenterActionType.NONE) {
      throw new Error(
        'sigv-file-center component requried action set EDIT or VIEW.'
      );
    }

    // 檢測是否為手機裝置
    const isMobile = /Android|iPhone|iPad|iPod/i.test(
      window.navigator.userAgent
    );

    // 若為手機裝置，移除 .msg 類型
    this.accept = isMobile
      ? this.accept.replace(/,?\s*\.msg\s*/, '')
      : this.accept;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit() {
    if (this.pdfDoc) {
      this.renderPdfPage();
    }
  }

  get isViewMode(): boolean {
    return this.action === FileCenterActionType.VIEW;
  }

  getFileDetails(fileDetailIds: string[]): Observable<FileInfo[]> {
    if (fileDetailIds && fileDetailIds.length) {
      return this.apiFileCenterService.getFileDetails(fileDetailIds).pipe(
        mergeMap((fileInfos) => {
          const fileDetails$ = fileInfos.map(
            async (file): Promise<FileInfo> => {
              const isImg: boolean = isImageFileExtension(file.extension);
              file.isDownloadable = !this.fileDetailIdsDisableDownload.includes(
                file.fileDetailId
              );
              file.rotation = 0;
              file.maxZoom = 3;
              if (isImg) {
                const imgUrl = this.apiFileCenterService.getFileDownloadUrl(
                  file.fileDetailId,
                  this.userProfileService.currentUserValue?.userNo ?? ''
                );
                const thumbnailSrc = await this.generateThumbnail(
                  imgUrl,
                  100,
                  100
                );
                file.previewSrc = imgUrl;
                file.thumbnailImageSrc = thumbnailSrc;
              }
              return file;
            }
          );

          return from(Promise.all(fileDetails$)) as Observable<FileInfo[]>;
        }),
        takeUntil(this.destroy$)
      );
    } else {
      return of([]);
    }
  }

  /**
   * 壓縮Galleria小圖
   *
   * @param {*} url
   * @param {number} width
   * @param {number} height
   * @return {*}  {Promise<string>}
   * @memberof FileCenterComponent
   */
  generateThumbnail(url: any, width: number, height: number): Promise<string> {
    return new Promise((resolve, reject) => {
      this.http
        .get(url, { observe: 'response', responseType: 'blob' })
        .toPromise()
        .then((response) => response.body as Blob)
        .then((blob) => {
          const objectUrl = URL.createObjectURL(blob);
          const img = new Image();
          img.crossOrigin = 'Anonymous'; // 避免跨域問題
          img.src = objectUrl; // 設定圖片來源

          img.onload = () => {
            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d');

            canvas.width = width;
            canvas.height = height;

            const imgWidth = img.width;
            const imgHeight = img.height;

            // 計算縮放比例，保持圖片比例
            const widthRatio = width / imgWidth;
            const heightRatio = height / imgHeight;
            const scale = Math.min(widthRatio, heightRatio);

            const scaledWidth = imgWidth * scale;
            const scaledHeight = imgHeight * scale;

            // 讓圖片居中
            const offsetX = (width - scaledWidth) / 2;
            const offsetY = (height - scaledHeight) / 2;

            if (context) {
              context.clearRect(0, 0, canvas.width, canvas.height);
              context.drawImage(
                img,
                offsetX,
                offsetY,
                scaledWidth,
                scaledHeight
              );

              // 轉換 Canvas 圖片為 Data URL
              const thumbnailUrl = canvas.toDataURL('image/jpeg');
              resolve(thumbnailUrl);
            } else {
              reject(new Error('Failed to get canvas context'));
            }

            // 釋放記憶體，防止 Object URL 佔用過多資源
            URL.revokeObjectURL(objectUrl);
          };

          img.onerror = (error) => {
            URL.revokeObjectURL(objectUrl);
            reject(error);
          };
        })
        .catch((error) => reject(error));
    });
  }

  isShowFileTypeIcon(extension: string): boolean {
    return !isImageFileExtension(extension);
  }

  getFileTypeIconStyleClassName(extension: string): string {
    return getFileCategoryCLassNameByExtension(extension);
  }

  isAttachmentsTableEditing(): boolean {
    return Object.entries(this.clonedAttachments).length > 0;
  }

  isRowViewOnly(id: string): boolean {
    return this.fileDetailIdsViewOnly.includes(id);
  }

  onRowEditInit(file: FileInfo): void {
    this.clonedAttachments[file.fileDetailId] = { ...file };
  }

  onRowEditSave(file: FileInfo, index: number): void {
    if (file.userSideName) {
      const filenameArray: string[] = [];
      filenameArray.push(file.userSideName);
      if (
        this.validateFileName(filenameArray) &&
        this.validateFileNameMaxLength(filenameArray)
      ) {
        this.apiFileCenterService
          .updateFileDetail({
            fileDetailId: file.fileDetailId,
            fileName: file.userSideName,
            description: file.description,
            editor: this.userProfileService.currentUserValue?.userNo || '',
          })
          .subscribe({
            next: () => {
              const updateTarget = this.attachments.find(
                (item) => item.fileDetailId === file.fileDetailId
              );
              if (updateTarget) {
                updateTarget.fileName = file.userSideName;
                updateTarget.description = file.description;
                updateTarget.editor =
                  this.userProfileService.currentUserValue?.userNo || '';
              }
              this.attachmentsChange.emit(this.attachments);

              this.translateService
                .get('SigvLibrary.SaveSuccess')
                .subscribe((t) => {
                  this.toastService.success(t);
                });
            },
            error: (error) => {
              this.logger.error(
                `Update file[${file.fileDetailId}] fail:`,
                error
              );
            },
          });
      } else {
        this.attachmentTable!.initRowEdit(file);
        return;
      }
    } else {
      const errorFields: string[] = [];
      if (!file.userSideName) {
        errorFields.push('SigvLibrary.FileName');
      }
      if (errorFields.length > 0) {
        this.translateService
          .get(['SigvLibrary.MustNotBeBlank', ...errorFields])
          .subscribe((t) => {
            const errorMessages: ErrorDialogMessage[] = [];
            errorFields.forEach((field) => {
              errorMessages.push({
                description: t[field],
                errorMessage: t['SigvLibrary.MustNotBeBlank'],
              } as ErrorDialogMessage);
            });

            if (errorMessages.length > 0) {
              this.dialogService.showErrorDialog(errorMessages);
            }
          });
      }
      this.attachments[index] = this.clonedAttachments[file.fileDetailId];
    }
    delete this.clonedAttachments[file.fileDetailId];
  }

  onRowEditCancel(file: FileInfo, index: number): void {
    this.attachments[index] = this.clonedAttachments[file.fileDetailId];
    delete this.clonedAttachments[file.fileDetailId];
  }

  async selectedUploadFiles(event: any): Promise<void> {
    const files = (event.target as HTMLInputElement).files;
    if (files && files.length > 0) {
      const uploadFiles = Array.from(files);
      const isFileSizeValidate = this.validateFilesMaxSize(
        uploadFiles.map((file) => file.size)
      );
      const isFileTypeValidate = this.validateAcceptFileType(
        uploadFiles.map((file) => file.name)
      );
      const isFileNameValidate = this.validateFileName(
        uploadFiles.map((file) => file.name.replace(/\.[^/.]+$/, ''))
      );
      const isFileNameMaxLengthValidate = this.validateFileNameMaxLength(
        uploadFiles.map((file) => file.name.replace(/\.[^/.]+$/, ''))
      );

      if (!isFileSizeValidate) {
        this.dialogService.showErrorDialog([
          {
            description: this.translateService.instant(
              'SigvLibrary.UploadFilesLimitSize',
              { fileSize: `${this.uploadFileSizeLimit} MB` }
            ),
          },
        ]);
        // 檔案不通過檢核，清空相關檔案
        event.target.value = null; // <=== 必要，會影響下次觸發 onChange 條件
        event.target.files = null; // <=== 非必要但清空比較好
        event.currentTarget.files = null; // <=== 非必要但清空比較好
        return;
      }

      if (!isFileTypeValidate) {
        this.dialogService.showErrorDialog([
          {
            description: this.translateService.instant(
              'SigvLibrary.UploadAcceptFileType'
            ),
          },
        ]);
        // 檔案不通過檢核，清空相關檔案
        event.target.value = null; // <=== 必要，會影響下次觸發 onChange 條件
        event.target.files = null; // <=== 非必要但清空比較好
        event.currentTarget.files = null; // <=== 非必要但清空比較好
        return;
      }

      // 檔名檢核
      if (!isFileNameValidate) {
        // 檔案不通過檢核，清空相關檔案
        event.target.value = null; // <=== 必要，會影響下次觸發 onChange 條件
        event.target.files = null; // <=== 非必要但清空比較好
        event.currentTarget.files = null; // <=== 非必要但清空比較好
        return;
      }

      // 檔名長度檢核
      if (!isFileNameMaxLengthValidate) {
        // 檔案不通過檢核，清空相關檔案
        event.target.value = null; // <=== 必要，會影響下次觸發 onChange 條件
        event.target.files = null; // <=== 非必要但清空比較好
        event.currentTarget.files = null; // <=== 非必要但清空比較好
        return;
      }

      const processed = await Promise.all(
        uploadFiles.map(async (file, index) => {
          const extension = file.name.split('.').pop() || '';
          let previewSrc: SafeUrl = '';
          if (isImageFileExtension(extension)) {
            previewSrc = this.sanitizer.bypassSecurityTrustUrl(
              getImagePreviewSrcByBlob(file)
            );
          }

          // 檢查是否使用自訂檔名格式
          if (
            this.customFormatFileName &&
            this.customFormatFileName(file, index, extension) !== file.name
          ) {
            const formattedName = this.customFormatFileName(
              file,
              index,
              extension
            );
            if (formattedName !== null && formattedName !== file.name) {
              // Create a new File object with the new name
              file = new File([file], formattedName, {
                type: file.type,
                lastModified: file.lastModified,
              });
            }
          }

          if (this.checkIsPhoto(extension)) {
            const isValid = await this.validateImage(file);
            if (!isValid) {
              setTimeout(() => {
                this.dialogService.showErrorDialog([
                  {
                    description:
                      this.translateService.instant('Invalid image file'),
                  },
                ]);
              }, 100);

              return null;
            }
          }

          return {
            fileId: `uploadFile_${index}`,
            file,
            extension,
            description: '',
            previewSrc,
          };
        })
      );
      this.uploadFiles = processed.filter((f): f is any => f !== null);
      this.buildUploadFileForm();

      if (this.uploadFiles.length > 0) {
        this.isShowFilesUploadDialog = true;
        this.changeDetectorRef.markForCheck();
      }
    }
  }

  private validateAcceptFileType(fileNames: string[]): boolean {
    const accepts = this.accept
      .split(',')
      .map((extensionName) => {
        if (extensionName) {
          return extensionName.replace(/\./gm, '').trim();
        } else {
          return '';
        }
      })
      .filter((e) => !!e);
    if (accepts && accepts.length > 0) {
      const ruleString = `\\.(${accepts.join('|')})+$`;
      const regex = new RegExp(ruleString);

      return fileNames.every((fileName) => {
        if (fileName) {
          const checkFileName = fileName.toLocaleLowerCase();
          return regex.test(checkFileName);
        } else {
          return false;
        }
      });
    }
    return true;
  }

  deleteFiles(): void {
    const editableRows = this.selectedAttachments.filter(
      (file) => !this.fileDetailIdsViewOnly.includes(file.fileDetailId)
    );
    if (editableRows.length > 0) {
      editableRows.forEach((file) => {
        const delIndex = this.attachments.findIndex(
          (attachment) =>
            attachment.fileDetailId === file.fileDetailId &&
            !this.fileDetailIdsViewOnly.includes(attachment.fileDetailId)
        );
        this.attachments.splice(delIndex, 1);
      });

      this.updateTableStatus();
      this.attachmentsChange.emit(this.attachments);
    }

    this.selectedAttachments = [];
    this.changeDetectorRef.markForCheck();
    this.isSelectedSomeSelectableRow = false;
  }

  resetUploadFilesInput(): void {
    if (this.uploadFilesInput) {
      this.uploadFilesInput.nativeElement.value = '';
    }
  }

  submitUploadFiles(): void {
    this.uploadFilesForm.markAllAsTouched();
    if (this.uploadFilesForm.valid) {
      // 只支援單筆檔案時，重置編輯模式，清空原有檔案
      if (!this.multiple) {
        this.attachments.splice(this.attachmentImages.length);
        this.cancelTableEditMode();
      }

      const uploadJobs$ = this.uploadFiles.map((item, index) => {
        return this.apiFileCenterService.uploadFile({
          domain: this.domain,
          subDomain: this.subDomain,
          creator: this.userProfileService.currentUserValue?.userNo || '',
          description: this.uploadFilesForm.get(item.fileId)?.value || '',
          file: item.file,
          sortIndex: index,
        });
      });

      forkJoin([...uploadJobs$]).subscribe((uploadResults) => {
        if (uploadResults && uploadResults.length > 0) {
          this.getFileDetails(uploadResults).subscribe((files) => {
            if (files && files.length > 0) {
              this.attachments.push(...files);
              this.updateTableStatus();
              this.attachmentsChange.emit(this.attachments);
              this.changeDetectorRef.markForCheck();
            }

            // init upload file and close upload file dialog
            this.resetUploadFilesInput();
            this.isShowFilesUploadDialog = false;
            this.uploadFiles = [];
            this.uploadFilesForm = new FormGroup({});
          });
        } else {
          this.resetUploadFilesInput();
          this.isShowFilesUploadDialog = false;
        }
      });
    }
  }

  cancelUploadFiles(): void {
    this.resetUploadFilesInput();
    this.isShowFilesUploadDialog = false;
    this.uploadFiles = [];
    this.uploadFilesForm = new FormGroup({});
  }

  downloadFile(fileInfo: FileInfo): void {
    if (this.fileDetailIdsDisableDownload.includes(fileInfo.fileDetailId)) {
      return;
    }

    this.apiFileCenterService
      .getFileDownloadAsync({
        fileDetailId: fileInfo.fileDetailId,
        downloader: this.userProfileService.currentUserValue?.userNo || '',
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe((file) => {
        if (file) {
          let fileName = fileInfo.userSideName;
          const fileExtension =
            fileInfo.extension.indexOf('.') > -1
              ? fileInfo.extension
              : `.${fileInfo.extension}`;
          const extensionNameRegExp = new RegExp(`${fileExtension}$`, 'g');
          if (!extensionNameRegExp.test(fileInfo.userSideName)) {
            fileName = fileName + fileExtension;
          }

          fileDownload(file, fileName);
          this.fileDownloaded.emit([fileInfo.fileDetailId]);
        } else {
          this.translateService
            .get('SigvLibrary.DownloadFail')
            .subscribe((t) => {
              this.dialogService.showErrorDialog([
                {
                  description: t,
                },
              ]);
            });
        }
      });
  }

  buildUploadFileForm(): void {
    if (this.uploadFiles && this.uploadFiles.length > 0) {
      this.uploadFiles.forEach((uploadFile) => {
        this.uploadFilesForm.addControl(
          uploadFile.fileId,
          new FormControl(uploadFile.description, [])
        );
      });
    } else {
      this.uploadFilesForm = new FormGroup({});
    }
  }

  showGalleria(fileDetailId: string): void {
    this._isShowGalleria = true;
    this.attachmentImages = this.attachments.filter((attachment) => {
      return isImageFileExtension(attachment.extension);
    });
    this.galleriaActiveIndex = this.attachmentImages.findIndex(
      (attachment) => attachment.fileDetailId === fileDetailId
    );
  }

  cancelTableEditMode(): void {
    if (this.attachmentTable) {
      if (Object.keys(this.clonedAttachments).length > 0) {
        for (const [, value] of Object.entries(this.clonedAttachments)) {
          const editIndex = this.attachments.findIndex(
            (item) => item.fileDetailId === value.fileDetailId
          );
          // 回復編輯 row 之資料，在編輯狀態下未按下確定即離開應回復資料並關閉編輯模式
          this.onRowEditCancel(value, editIndex);
          // 關閉編輯模式
          this.attachmentTable.cancelRowEdit(value);
        }
      }
      this.changeDetectorRef.markForCheck();
    }
  }

  openUploadFile(event?: Event): void {
    event?.preventDefault();
    this.uploadFilesInput.nativeElement.click();
  }

  selectChangeHandler(): void {
    // 選取所有可選取資料，加入所有可選取與不可選取資料
    // 達到全選被勾選的效果
    let unSelectableFileDetailIds: string[] = [];
    switch (this.action) {
      case FileCenterActionType.VIEW:
        unSelectableFileDetailIds = this.fileDetailIdsDisableDownload;
        break;
      case FileCenterActionType.EDIT:
        unSelectableFileDetailIds = this.fileDetailIdsViewOnly;
        break;
    }

    if (
      this.selectedAttachments.length > 0 &&
      this.selectedAttachments.length ===
        this.attachments.filter(
          (item) => !unSelectableFileDetailIds.includes(item.fileDetailId)
        ).length
    ) {
      this.selectedAttachments.push(
        ...this.attachments.filter(
          (item) =>
            unSelectableFileDetailIds.includes(item.fileDetailId) &&
            !this.selectedAttachments
              .map((f) => f.fileDetailId)
              .includes(item.fileDetailId)
        )
      );
    }

    // 所有被選取的資料都是不可編輯的資料，清除所有選取資料
    // 達到全選被取消勾選的效果
    if (
      this.selectedAttachments.every((item) =>
        unSelectableFileDetailIds.includes(item.fileDetailId)
      )
    ) {
      this.selectedAttachments = [];
    }

    // 選取資料中可選取的資料
    const selectedSelectableFileInfo = this.selectedAttachments.filter(
      (item) => !unSelectableFileDetailIds.includes(item.fileDetailId)
    );

    // 更新表頭全選 checkobx 呈現勾選部份項目的狀態
    this.isSelectedSomeSelectableRow =
      selectedSelectableFileInfo.length > 0 &&
      selectedSelectableFileInfo.length <
        this.attachments.filter(
          (item) => !unSelectableFileDetailIds.includes(item.fileDetailId)
        ).length;

    this.changeDetectorRef.markForCheck();
  }

  downloadFiles(): void {
    const downloadFilesId = this.selectedAttachments
      .filter(
        (file) => !this.fileDetailIdsDisableDownload.includes(file.fileDetailId)
      )
      .map((file) => file.fileDetailId);
    this.apiFileCenterService
      .getFilesBundleDownloadAsync({
        fileDetailIds: [...downloadFilesId],
        downloader: this.userProfileService.currentUserValue?.userNo || '',
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response) => {
          if (response) {
            try {
              fileDownload(response, this.downloadBundleFileName);
              this.fileDownloaded.emit(downloadFilesId);
            } catch (error: unknown) {
              this.logger.error(
                'ApiFileCenterService getFilesBundleDownloadAsync Error:',
                error
              );
              this.dialogService.showErrorDialog(
                [
                  {
                    description: this.translateService.instant(
                      'SigvLibrary.DownloadFail'
                    ),
                  },
                ],
                this.translateService.instant('SigvLibrary.Error')
              );
            }
          }
        },
        (error) => {
          this.logger.error(
            'ApiFileCenterService getFilesBundleDownloadAsync Error:\n\n',
            error
          );
          this.dialogService.showErrorDialog(
            [
              {
                description: this.translateService.instant(
                  'SigvLibrary.DownloadFail'
                ),
              },
            ],
            this.translateService.instant('SigvLibrary.Error')
          );
        }
      );
  }

  private updateTableStatus(): void {
    this.hasEditableAttachments = this.attachments.some(
      (file) => !this.fileDetailIdsViewOnly.includes(file.fileDetailId)
    );
  }

  private validateFilesMaxSize(fileSizes: number[]): boolean {
    if (fileSizes && fileSizes.length > 0) {
      // const totalSize = fileSizes.reduce((a, b) => a + b, 0);
      const limitSize = this.uploadFileSizeLimit * (1024 * 1024);
      // bug/#17805 檔案上傳限制應為'單檔大小'非'總檔大小加總'
      return !fileSizes.find((size) => size >= limitSize);
      // return totalSize <= limitSize;
    } else {
      return true;
    }
  }

  /**
   * 以載入瀏覽器的方式驗證檔案毀損
   *
   * @param {File} res
   * @return {Promise<boolean>}
   * @memberof FileTransferService
   */
  validateImage(file: File): Promise<boolean> {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
      img.src = URL.createObjectURL(file);
    });
  }

  /**
   * 是否為圖片類型 icon
   *
   * @param {FileInfo} fileInfo
   * @return {*}  {boolean}
   * @memberof FileUploadComponent
   */
  checkIsPhoto(extension: string): boolean {
    const category = extension?.replace('.', '').toLowerCase();
    if (this.isNullOrEmpty(category)) {
      return false;
    }
    return IMG_EXT_ICON_MAP.has(category!);
  }
  isNullOrEmpty(value: any): boolean {
    return (
      typeof value === 'undefined' ||
      value == null ||
      (typeof value === 'string' && value === '')
    );
  }

  /**
   * 檔名格式判斷
   *
   * @param {FileInfo} fileInfo
   * @return {*}  {boolean}
   * @memberof FileCenterComponent
   */
  validateFileName(fileNames: string[]): boolean {
    const regex = /^[^<>:"\/\\|?*\x00-\x1F]+$/;
    return fileNames.every((name) => {
      if (regex.test(name) === false) {
        this.dialogService.showErrorDialog([
          {
            description: this.translateService.instant(
              'SigvLibrary.UploadAcceptFileName'
            ),
          },
        ]);
        return false;
      } else {
        return true;
      }
    });
  }

  /**
   * 檔名不得超過255字元
   *
   * @private
   * @param {string[]} fileNames
   * @return {*}  {boolean}
   * @memberof FileCenterComponent
   */
  private validateFileNameMaxLength(fileNames: string[]): boolean {
    return fileNames.every((name) => {
      if (name.length > 255) {
        this.dialogService.showErrorDialog([
          {
            description: this.translateService.instant(
              'SigvLibrary.UploadFileNameMaxLength'
            ),
          },
        ]);
        return false;
      } else {
        return true;
      }
    });
  }

  /**
   * R202412065178 滑鼠滾動縮放圖片
   *
   * @param {WheelEvent} event
   * @memberof FileCenterComponent
   */
  onWheel(event: WheelEvent) {
    event.preventDefault();
    const minZoom = 1;

    if (event.deltaY < 0) {
      this.zoomIn();
    } else {
      this.zoomOut();
    }

    if (this.zoomLevel === minZoom) {
      this.resetImage(this.galleriaActiveIndex);
    }

    this.clampTranslate();
  }

  /**
   * 圖片載入完成時，取得圖片和容器尺寸
   *
   * @param {Event} event
   * @memberof FileCenterComponent
   */
  onImageLoad(event: Event) {
    const imgElement = event.target as HTMLImageElement;
    this.imageWidth = imgElement.naturalWidth;
    this.imageHeight = imgElement.naturalHeight;

    // 更新 displayImages 中相應圖片的原始寬度和高度
    this.attachmentImages[this.galleriaActiveIndex].naturalWidth =
      this.imageWidth;
    this.attachmentImages[this.galleriaActiveIndex].naturalHeight =
      this.imageHeight;

    // 計算容器的寬度和高度
    const container = imgElement.closest(
      '.custom-image-container'
    ) as HTMLElement;
    this.containerWidth = container ? container.clientWidth : 0;
    this.containerHeight = container ? container.clientHeight : 0;
  }

  /**
   * 滑鼠左鍵按下後，開始拖曳圖片
   *
   * @param {MouseEvent} event
   * @memberof FileCenterComponent
   */
  startDragging(event: MouseEvent) {
    event.preventDefault();

    if (this.zoomLevel > 1) {
      this.isDragging = true;
      this.startX = event.clientX;
      this.startY = event.clientY;

      const container = event.currentTarget as HTMLElement;
      if (container) {
        container.style.cursor = 'grabbing';
      }
    }
  }

  /**
   * 滑鼠左鍵按住拖曳圖片
   *
   * @param {MouseEvent} event
   * @memberof FileCenterComponent
   */
  onDragging(event: MouseEvent) {
    if (this.isDragging) {
      const deltaX = event.clientX - this.startX;
      const deltaY = event.clientY - this.startY;

      this.translateX = this.lastTranslateX + deltaX;
      this.translateY = this.lastTranslateY + deltaY;

      // 限制移動範圍
      this.clampTranslate();
    }
  }

  /**
   * 滑鼠鬆開，停止移動圖片
   *
   * @memberof FileCenterComponent
   */
  stopDragging() {
    if (this.isDragging) {
      this.isDragging = false;
      this.lastTranslateX = this.translateX;
      this.lastTranslateY = this.translateY;

      const container = document.querySelector(
        '.custom-image-container'
      ) as HTMLElement;
      if (container) {
        container.style.cursor = 'grab';
      }
    }
  }

  /**
   * 限制圖片移動範圍
   *
   * @memberof FileCenterComponent
   */
  clampTranslate() {
    const imgageWidth =
      this.containerWidth > this.imageWidth
        ? this.containerWidth
        : this.imageWidth;
    const imgageHeight =
      this.containerHeight > this.imageHeight
        ? this.containerHeight
        : this.imageHeight;

    const scaledWidth = imgageWidth * this.zoomLevel;
    const scaledHeight = imgageHeight * this.zoomLevel;

    // 計算最大平移範圍
    const maxTranslateX = Math.max(0, (scaledWidth - this.containerWidth) / 2);
    const maxTranslateY = Math.max(
      0,
      (scaledHeight - this.containerHeight) / 2
    );

    // 限制圖片移動範圍
    this.translateX = Math.min(
      maxTranslateX,
      Math.max(-maxTranslateX, this.translateX)
    );
    this.translateY = Math.min(
      maxTranslateY,
      Math.max(-maxTranslateY, this.translateY)
    );
  }

  /**
   * 重置圖片縮放大小與位置
   *
   * @param {number} newIndex
   * @memberof FileCenterComponent
   */
  resetImage(newIndex: number) {
    this.zoomLevel = 1;
    this.translateX = 0;
    this.translateY = 0;
    this.lastTranslateX = 0;
    this.lastTranslateY = 0;
    this.galleriaActiveIndex = newIndex;
  }

  /**
   * 最大放大倍率為圖片原始大小
   *
   * @memberof FileCenterComponent
   */
  zoomIn() {
    this.adjustZoom('in');
  }

  /**
   * 最小縮放為畫面顯示大小
   *
   * @memberof FileCenterComponent
   */
  zoomOut() {
    this.adjustZoom('out');
  }

  /**
   * 計算圖片縮放比例
   *
   * @param {('in' | 'out')} zoomDirection
   * @memberof FileCenterComponent
   */
  adjustZoom(zoomDirection: 'in' | 'out') {
    const imgElement = this.attachmentImages[this.galleriaActiveIndex];
    const originalWidth = imgElement.naturalWidth!;
    const originalHeight = imgElement.naturalHeight!;

    // 計算容器和圖片的比例
    const containerAspectRatio = this.containerWidth / originalWidth;
    const containerHeightRatio = this.containerHeight / originalHeight;

    const containerToImageRatio = Math.min(
      containerAspectRatio,
      containerHeightRatio
    );
    const imageToContainerRatio = Math.max(
      originalWidth / this.containerWidth,
      originalHeight / this.containerHeight
    );

    // 計算最大和最小縮放比例
    let maxZoom = 3;
    let minZoom = 1;

    if (containerToImageRatio > 1) {
      // 容器尺寸比圖片大
      maxZoom = containerToImageRatio > 3 ? containerToImageRatio : 3;
      minZoom = Math.min(
        originalWidth / this.containerWidth,
        originalHeight / this.containerHeight
      ); // 圖片實際大小
    } else {
      // 圖片比容器大
      maxZoom = imageToContainerRatio > 3 ? imageToContainerRatio : 3;
      minZoom = 1;
    }

    // 縮放操作
    if (zoomDirection === 'in') {
      this.zoomLevel = Math.min(this.zoomLevel + 0.1, maxZoom);
    } else if (zoomDirection === 'out') {
      this.zoomLevel = Math.max(this.zoomLevel - 0.1, minZoom);
    }

    // 在調整縮放後，更新拖曳範圍
    this.clampTranslate();
  }

  /**
   * 向左旋轉 90 度
   *
   * @memberof FileCenterComponent
   */
  rotateLeft() {
    this.attachmentImages[this.galleriaActiveIndex].rotation =
      (this.attachmentImages[this.galleriaActiveIndex].rotation! - 90) % 360;
  }

  /**
   * 向右旋轉 90 度
   *
   * @memberof FileCenterComponent
   */
  rotateRight() {
    this.attachmentImages[this.galleriaActiveIndex].rotation =
      (this.attachmentImages[this.galleriaActiveIndex].rotation! + 90) % 360;
  }

  /**
   * 開啟預覽PDF檔案
   *
   * @param {FileDisplayInfo} fileInfo
   * @memberof FileUploadComponent
   */
  openPdfFile(fileInfo: FileInfo): void {
    const extName = fileInfo.extension?.toLowerCase();

    const fileUrl = this.apiFileCenterService.getFileDownloadUrl(
      fileInfo.fileDetailId,
      this.userProfileService.currentUserValue?.userNo ?? ''
    );

    if (extName === '.pdf') {
      this.previewPdf(fileUrl);
    }
  }

  /**
   * 判斷是否為PDF檔案
   *
   * @param {FileDisplayInfo} fileInfo
   * @return {*}  {boolean}
   * @memberof FileUploadComponent
   */
  isPDF(fileInfo: FileInfo): boolean {
    const extName = fileInfo.extension?.toLowerCase();
    return extName === '.pdf';
  }

  /**
   * 預覽 PDF 文件
   *
   * @param {string} fileUrl
   * @memberof FileCenterComponent
   */
  async previewPdf(fileUrl: string) {
    this.showPdfPreview = true;

    try {
      // 初始化 PDF 文件
      const arryBuffer = await this.http
        .get(fileUrl, { responseType: 'arraybuffer' })
        .toPromise();
      const loadingTask = pdf.getDocument(new Uint8Array(arryBuffer));
      this.pdfDoc = await loadingTask.promise;

      // 初始化相關屬性
      this.currentPDFPage = 1;
      this.scalePDF = 1.5;
      this.rotationPDF = 0;

      this.changeDetectorRef.detectChanges();

      // 渲染 PDF 頁面
      setTimeout(() => {
        this.renderPdfPage();
      }, 0);
    } catch (error) {
      console.error('Error loading PDF:', error);
    }
  }

  /**
   * 渲染指定頁面
   *
   * @return {*}
   * @memberof FileCenterComponent
   */
  async renderPdfPage() {
    if (!this.pdfDoc) return;

    const canvases = this.pdfCanvases?.toArray();
    if (!canvases || canvases.length === 0) {
      console.error('No canvases found!');
      return;
    }

    for (let pageNumber = 1; pageNumber <= this.pdfDoc.numPages; pageNumber++) {
      const page = await this.pdfDoc.getPage(pageNumber);
      const viewport = page.getViewport({
        scale: this.scalePDF,
        rotation: this.rotationPDF,
      });
      const canvas = canvases[pageNumber - 1]?.nativeElement;

      if (canvas) {
        const context = canvas.getContext('2d');
        if (!context) continue;

        canvas.width = viewport.width;
        canvas.height = viewport.height;

        const renderContext = {
          canvasContext: context,
          viewport: viewport,
        };

        await page.render(renderContext).promise;
      }
    }
  }

  /**
   * PDF放大功能
   *
   * @memberof FileCenterComponent
   */
  async zoomInPDF() {
    const maxZoom = 3;
    this.scalePDF = Math.min(this.scalePDF + 0.1, maxZoom);
    if (!this.isRendering) {
      this.isRendering = true;
      await this.renderPdfPage();
      this.isRendering = false;
    }
  }

  /**
   * PDF縮小功能
   *
   * @memberof FileCenterComponent
   */
  async zoomOutPDF() {
    const minZoom = 1;
    this.scalePDF = Math.max(this.scalePDF - 0.1, minZoom);
    if (!this.isRendering) {
      this.isRendering = true;
      await this.renderPdfPage();
      this.isRendering = false;
    }
  }

  /**
   * PDF向右旋轉功能
   *
   * @memberof FileCenterComponent
   */
  async rotatePDFright() {
    this.rotationPDF = (this.rotationPDF + 90) % 360;
    if (!this.isRendering) {
      this.isRendering = true;
      await this.renderPdfPage();
      this.isRendering = false;
    }
  }

  /**
   * PDF向左旋轉功能
   *
   * @memberof FileCenterComponent
   */
  async rotatePDFleft() {
    this.rotationPDF = (this.rotationPDF - 90) % 360;
    if (!this.isRendering) {
      this.isRendering = true;
      await this.renderPdfPage();
      this.isRendering = false;
    }
  }

  /**
   * 滾動縮放PDF
   *
   * @param {WheelEvent} event
   * @memberof FileCenterComponent
   */
  async handleMouseWheel(event: WheelEvent) {
    event.preventDefault();

    const zoomStep = 0.1;
    const maxZoom = 3;
    const minZoom = 1;

    if (event.deltaY < 0) {
      this.scalePDF = Math.min(this.scalePDF + zoomStep, maxZoom);
    } else {
      this.scalePDF = Math.max(this.scalePDF - zoomStep, minZoom);
    }

    // 只在前一次渲染完成後再開始新的渲染，避免滾動過快重複渲染
    if (!this.isRendering) {
      this.isRendering = true;
      requestAnimationFrame(async () => {
        await this.renderPdfPage();
        this.isRendering = false;
      });
    }
  }

  /**
   * 關閉 PDF 預覽
   *
   * @memberof FileCenterComponent
   */
  closePdfPreview() {
    this.showPdfPreview = false;
    this.scalePDF = 1.5;
    this.rotationPDF = 0;
    this.currentPDFPage = 1;
    this.pdfDoc = null;
  }
}
